/**
 * Verbatim copy for the campaign lifecycle action dialogs (Pause / Resume / Cancel / Archive /
 * Delete), lifted from `Campaign Status protoype/client-experience-prototype.html`. Centralised here
 * so the card's dialogs can't drift from the design. Amounts/dates are injected by the components.
 */

/** Tooltip on a card action the role allows but this campaign's group is outside the caller's reach. */
export const CAMPAIGN_ACTION_OUT_OF_SCOPE_TOOLTIP =
  "Only available for campaigns in the groups you manage";

/** The same tooltip for a View Only role, which manages no group. */
export const CAMPAIGN_ACTION_VIEW_ONLY_TOOLTIP =
  "You have view-only access, so you can't edit campaigns";

export const PAUSE_MODAL = {
  icon: "pause_circle",
  title: (name: string) => `Pause ${name}?`,
  // The "what pausing does" explanation — only shown when pausing a LIVE campaign (a scheduled/draft
  // pause has reached no recipient, so it pauses silently).
  body:
    "Pausing a live campaign stops any more emails going out, including reminder emails. If recipients " +
    "have already received their gift emails, pausing a campaign will not prevent them from viewing the " +
    "gift collection or redeeming a gift.",
  confirm: "Pause campaign",
  dismiss: "Cancel",
  toast: (name: string) => `Paused: ${name}`,
} as const;

export const RESUME_MODAL = {
  // The forced date picker, shown ONLY when the send date and/or expiry lapsed during the pause.
  title: "Set new dates to resume",
  calloutLead: "This campaign cannot simply pick up where it left off because ",
  calloutTail: ". Choose new dates to resume it.",
  reasonSendPassed: (date: string) => `its send date (${date}) has passed`,
  reasonActivationPassed: (date: string) => `its activation date (${date}) has passed`,
  reasonExpired: (date: string) => `its gift links expired on ${date}`,
  sendDateLabel: "Send date",
  activationDateLabel: "Link activation date",
  expirationDateLabel: "Expiration date",
  confirm: "Resume campaign",
  dismiss: "Cancel",
  errSendFuture: "Pick a future send date.",
  errExpiryRequired: "Pick an expiration date.",
  errExpiryAfterSend: "The expiration date must come after the send date.",
  toastResumed: (name: string) => `Resumed: ${name}`,
  toastRescheduled: (name: string) => `Rescheduled and resumed: ${name}`,
} as const;

export const CANCEL_MODAL = {
  icon: "event_busy",
  title: (name: string) => `Cancel ${name}?`,
  body:
    "This campaign hasn't sent yet. Canceling stops it for good: no gift emails or reminders go out, " +
    "and its gift links won't work.",
  confirm: "Cancel campaign",
  dismiss: "Keep campaign",
  moneyLabel: "What happens to the money",
  rowReturned: (wallet: string) => `Credits returned to ${wallet}`,
  rowInvoicedVoided: "Invoiced deposit voided",
  rowCeilingReleased: "Billable ceiling released from your credit limit",
  noteReturned: (earliest: string) =>
    `These come back as credits, not a refund to your card. They keep their original expiry (the ` +
    `earliest is ${earliest}) and can fund another campaign until then.`,
  noteNothingHeld: "Nothing was held for this campaign, so there are no credits to return.",
  warnExpired: (amount: string) =>
    `${amount} of these credits have already passed their expiry, so they go straight to your cause ` +
    `beneficiary.`,
  toast: (name: string) => `Canceled: ${name}`,
} as const;

export const ARCHIVE_MODAL = {
  icon: "inventory_2",
  title: "Archive this campaign?",
  body: (name: string, wasCanceled: boolean) =>
    `${name} ${wasCanceled ? "was canceled" : "has already gone out"}, so its history is kept. ` +
    `Archiving hides it from your campaign list — you can bring it back by filtering Status to Archived.`,
  confirm: "Archive campaign",
  dismiss: "Cancel",
  toast: (name: string) => `Archived: ${name}`,
} as const;

export const DELETE_MODAL = {
  icon: "delete_outline",
  draft: {
    title: "Delete this draft?",
    body: (name: string) =>
      `${name} and its recipient list will be removed from your campaigns. This can't be undone.`,
    confirm: "Delete draft",
    dismiss: "Keep draft",
  },
  canceled: {
    title: "Delete this campaign?",
    // `credits` is the already-returned amount string, or "" to omit the wallet clause.
    body: (name: string, credits: string) =>
      `${name} was canceled${
        credits ? ` and its ${credits} in credits are already back in your wallet` : ""
      }. Deleting it removes it from your campaigns. This can't be undone.`,
    confirm: "Delete campaign",
    dismiss: "Keep campaign",
  },
  toast: (name: string) => `Deleted: ${name}`,
} as const;
